import React, { useState } from 'react';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { CalendarType } from '@/types/sla';
import type { BusinessHoursCalendar, BusinessHours, CreateBusinessHoursCalendarRequest } from '@/types/sla';

type CalendarRequest = CreateBusinessHoursCalendarRequest & { businessHours: Partial<BusinessHours>[]; holidays: BusinessHoursCalendar['holidays'] };
interface Props {
  initialData?: BusinessHoursCalendar;
  onSubmit: (data: CalendarRequest) => Promise<void>;
  onCancel: () => void;
}
const days = [{ day: 1, name: 'Monday' }, { day: 2, name: 'Tuesday' }, { day: 3, name: 'Wednesday' }, { day: 4, name: 'Thursday' }, { day: 5, name: 'Friday' }, { day: 6, name: 'Saturday' }, { day: 0, name: 'Sunday' }];

export const BusinessHoursCalendarForm: React.FC<Props> = ({ initialData, onSubmit, onCancel }) => {
  const [name, setName] = useState(initialData?.name ?? '');
  const [description, setDescription] = useState(initialData?.description ?? '');
  const [timeZone, setTimeZone] = useState(initialData?.timeZone ?? 'Australia/Sydney');
  const [isActive, setIsActive] = useState(initialData?.isActive ?? true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hours, setHours] = useState(() => days.map(({ day }) => {
    const existing = initialData?.businessHours?.find(row => row.dayOfWeek === day && !row.overrideDate);
    return { ...existing, dayOfWeek: day, isBusinessDay: existing?.isBusinessDay ?? (!initialData && day > 0 && day < 6), startTime: existing?.startTime ?? '09:00:00', endTime: existing?.endTime ?? '17:00:00' };
  }));
  const updateDay = (day: number, patch: Partial<BusinessHours>) => setHours(rows => rows.map(row => row.dayOfWeek === day ? { ...row, ...patch } : row));
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (loading) return;
    setError(null);
    if (!name.trim()) { setError('Calendar name is required.'); return; }
    if (hours.some(row => row.isBusinessDay && row.startTime >= row.endTime)) {
      setError('Each open day must end after its start time.'); return;
    }
    setLoading(true);
    try {
      await onSubmit({ name: name.trim(), description, timeZone, isActive,
        isDefault: initialData?.isDefault ?? false, type: initialData?.type ?? CalendarType.Standard,
        businessHours: [...hours.map(row => ({ ...row, endTime: row.endTime.length === 5 ? `${row.endTime}:00` : row.endTime })), ...(initialData?.businessHours?.filter(row => row.overrideDate || !hours.some(edited => edited.id === row.id)) ?? [])],
        holidays: initialData?.holidays ?? [],
      });
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Failed to save business hours calendar.');
    } finally { setLoading(false); }
  };
  const zones = Array.from(new Set([timeZone, 'Australia/Sydney', 'Australia/Brisbane', 'Australia/Perth', 'UTC', 'America/New_York', 'America/Los_Angeles', 'Europe/London', 'Asia/Tokyo']));
  return <form onSubmit={submit} className="space-y-5">
    {error && <div role="alert" className="rounded bg-red-50 p-3 text-red-800">{error}</div>}
    <Input label="Calendar Name" value={name} onChange={event => setName(event.target.value)} required />
    <Input label="Description" value={description} onChange={event => setDescription(event.target.value)} />
    <label className="block text-sm font-medium">Time Zone
      <select value={timeZone} onChange={event => setTimeZone(event.target.value)} className="mt-1 block w-full rounded border p-2">{zones.map(zone => <option key={zone}>{zone}</option>)}</select>
    </label>
    <label className="flex items-center gap-2"><input type="checkbox" checked={isActive} onChange={event => setIsActive(event.target.checked)} />Active</label>
    <div className="flex flex-wrap items-center justify-between gap-2"><h3 className="font-semibold">Business Hours</h3>
      <Button type="button" variant="outline" size="sm" onClick={() => {
        const monday = hours.find(row => row.dayOfWeek === 1)!;
        setHours(rows => rows.map(row => row.dayOfWeek >= 2 && row.dayOfWeek <= 5 ? { ...row, startTime: monday.startTime, endTime: monday.endTime, isBusinessDay: monday.isBusinessDay } : row));
      }}>Copy Monday to All Weekdays</Button>
    </div>
    {days.map(({ day, name: dayName }) => {
      const row = hours.find(value => value.dayOfWeek === day)!;
      return <fieldset key={day} className="rounded border p-3"><legend className="px-1 text-sm font-medium">{dayName}</legend>
        <label className="flex items-center gap-2"><input aria-label={`${dayName} open`} type="checkbox" checked={row.isBusinessDay} onChange={event => updateDay(day, { isBusinessDay: event.target.checked })} />{row.isBusinessDay ? 'Open' : 'Closed'}</label>
        {row.isBusinessDay && <div className="mt-2 grid grid-cols-2 gap-3">
          <Input type="time" label={`${dayName} start`} value={row.startTime.slice(0, 5)} required onChange={event => updateDay(day, { startTime: `${event.target.value}:00` })} />
          <Input type="text" inputMode="numeric" pattern="(?:[01][0-9]|2[0-3]):[0-5][0-9]|24:00" placeholder="17:00" label={`${dayName} end`} value={row.endTime.slice(0, 5)} required onChange={event => updateDay(day, { endTime: event.target.value })} />
        </div>}
      </fieldset>;
    })}
    <div className="flex flex-wrap gap-3"><Button type="submit" disabled={loading}>{loading ? 'Saving...' : initialData ? 'Update Calendar' : 'Create Calendar'}</Button><Button type="button" variant="outline" onClick={onCancel} disabled={loading}>Cancel</Button></div>
  </form>;
};
